import { CallAPIGetPromise, CallAPIPostPromise } from "Common/commaon";
import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { sendDocToS3 } from "slices/thunk";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { IoTrashOutline } from "react-icons/io5";
import { Download, Pencil, Trash2, Upload } from "lucide-react";
import { TwoButtonAlert } from "Common/twoButtonAlert";
import "./category-master.css";

type CategoryItem = {
  cat_id: string | number;
  cat_name: string;
  cat_img?: string;
  cat_state?: string;
};

const getCategoryStatus = (value?: string) =>
  String(value || "").toLowerCase() === "inactive" ? "Inactive" : "Active";

const CategoryMasterModern = () => {
  const [category, setCategory] = useState("");
  const [status, setStatus] = useState("Yes");
  const [id, setId] = useState("");
  const [list, setList] = useState<CategoryItem[]>([]);
  const [refresh, setRefresh] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [saving, setSaving] = useState(false);
  const [alertDetails,setAlertDetails]= useState({
    status: false,
    title: "Warning!",
    message: "You’re about to delete your account forever,",
    icon: IoTrashOutline,
    button1Text: "Keep it",
    button2Text: "Delete",
    button1Style: "px-4 py-2 rounded-lg bg-gray-100 text-black hover:bg-gray-200 transition",
    button2Style: "px-4 py-2 rounded-lg bg-red-600 text-white hover:bg-red-700 transition",
    onButton1Click: () => console.log("Saved "),
    onButton2Click: () => console.log("Deleted "),
  })
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const handleExport = () => {
    if (!list || list.length === 0) {
      setError("No data to export");
      return;
    }
  
    // format data
    const formatted = list.map((item, index) => ({
      "S.No": index + 1,
      "Category Name": item.cat_name,
      "Status": getCategoryStatus(item.cat_state),
      "Image URL": item.cat_img
    }));
  
    // create worksheet
    const worksheet = XLSX.utils.json_to_sheet(formatted);
  
    // create workbook
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Categories");
  
    // generate buffer
    const excelBuffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array"
    });
  
    const data = new Blob([excelBuffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8"
    });
  
    saveAs(data, "categories.xlsx");
  
    setSuccess("Excel exported successfully 😌");
  };
  // ✅ Image Upload State
  const [categoryImage, setCategoryImage] = useState<string | null>(null);
  const [categoryImagePreview, setCategoryImagePreview] = useState<string>("");
  const [uploadingImage, setUploadingImage] = useState(false);

  useEffect(() => {
    CallAPIGetPromise("/service/getServices").then(
      (res: any) => {
        setList(res?.result || []);
        setId("");
        setCategory("");
        setStatus("Yes");
        setCategoryImage(null);
        setCategoryImagePreview("");
      }
    );
  }, [refresh]);

  const handleSaveCategory = () => {
    setError("");
    setSuccess("");
  
    if (!category.trim()) {
      setError("Category name is required");
      return;
    }
  
    if (!categoryImage) {
      setError("Please upload a category image");
      return;
    }
  
    if (saving) return;
  
    setSaving(true);

 const payload = {
  cat_id: id ? Number(id) : undefined,
    cat_name: category,
    cat_img: categoryImage || null, // 👈 S3 URL
    cat_state: status === "Yes" ? "Active" : "Inactive",
    cat_created_by: "admin", // optional
 }
    const apiCall = id
      ? CallAPIPostPromise("/service/v2/updateService", payload)
      : CallAPIPostPromise("/service/v2/createService", payload);

      apiCall
      .then(() => {
        setSuccess(id ? "Category updated successfully" : "Category created successfully");
        setRefresh((r) => !r);
      })
      .catch((err: any) => {
        setError(err?.message || "Something went wrong");
      })
      .finally(() => setSaving(false));
  };


  const dispatch = useDispatch() as any 

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingImage(true);
    dispatch(sendDocToS3("service", file))
      .then((url: string) => {
        setCategoryImage(url);
        setCategoryImagePreview(URL.createObjectURL(file));
      })
      .catch(() => setError("Unable to upload the category image"))
      .finally(() => setUploadingImage(false));
  
  };

  const handleRemoveImage = () => {
    setCategoryImage(null);
    setCategoryImagePreview("");
  };

  const resetForm = () => {
    setId("");
    setCategory("");
    setStatus("Yes");
    setCategoryImage(null);
    setCategoryImagePreview("");
    setError("");
  };
  useEffect(() => {
    if (error || success) {
      const timer = setTimeout(() => {
        setError("");
        setSuccess("");
      }, 3000);
  
      return () => clearTimeout(timer);
    }
  }, [error, success]);
  return (
    <div className="tailwind-prefix page-content category-master-page">
      <div className="category-master-shell space-y-6">
          <div className="flex justify-end">
            <button
              type="button"
              onClick={handleExport}
              className="category-master-export inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-semibold text-gray-700 transition hover:bg-gray-50"
            >
              <Download size={16} aria-hidden="true" />
              Export Excel
            </button>
          </div>
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg text-sm" role="alert">
              {error}
            </div>
          )}

          {success && (
            <div className="bg-green-50 border border-green-200 text-green-600 px-4 py-3 rounded-lg text-sm" role="status">
              {success}
            </div>
          )}
          {/* Add / Edit Card */}
          <div className="bg-white rounded-2xl shadow-sm border p-6 category-master-editor">
            <div className="flex flex-wrap items-start justify-between gap-3 border-b border-gray-100 pb-4">
              <div>
                <h2 className="mb-0 text-lg font-semibold text-gray-900">{id ? "Edit category" : "Add category"}</h2>
                <p className="mb-0 mt-1 text-sm text-gray-500">Create and manage service categories shown across the platform.</p>
              </div>
              {id && (
                <button type="button" onClick={resetForm} className="category-editor-secondary rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-600 hover:bg-gray-50">
                  Cancel editing
                </button>
              )}
            </div>

            <div className="category-master-form-grid">
              <div>
                <label htmlFor="category-name" className="mb-2 block text-sm font-medium text-gray-700">Category name</label>
                <input
                  id="category-name"
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                  placeholder="Enter category name"
                  className="w-full border rounded-lg px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-black"
                />
              </div>

              <div>
                <label htmlFor="category-status" className="mb-2 block text-sm font-medium text-gray-700">Status</label>
                <select
                  id="category-status"
                  value={status}
                  onChange={(e) => setStatus(e.target.value)}
                  className="w-full border rounded-lg px-4 py-2.5"
                >
                  <option value="Yes">Active</option>
                  <option value="No">Inactive</option>
                </select>
              </div>
            </div>

            <div className="category-master-form-footer">
              <div>
                <span className="block text-sm font-medium text-gray-700 mb-2">Category image</span>

              <div className="flex items-center gap-4">
                <label className="cursor-pointer inline-flex items-center gap-2 px-4 py-2 rounded-lg border bg-gray-50 hover:bg-gray-100 transition text-sm">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageChange}
                    className="hidden"
                  />
                  <Upload size={16} aria-hidden="true" />
                  {uploadingImage ? "Uploading..." : "Upload image"}
                </label>

                {categoryImagePreview && (
                  <div className="flex items-center gap-3">
                    <img
                      src={categoryImagePreview}
                      alt={`${category || "Category"} preview`}
                      className="w-14 h-14 rounded-lg object-cover border"
                    />

                    <button
                      type="button"
                      onClick={handleRemoveImage}
                      className="text-sm bg-transparent border-0 text-red-500 hover:text-red-700"
                    >
                      Remove
                    </button>
                  </div>
                )}
              </div>

              {!categoryImagePreview && (
                <p className="text-xs text-gray-400 mt-2">
                  Upload JPG/PNG/WebP image for this category
                </p>
              )}
              </div>

              <button
                type="button"
                onClick={handleSaveCategory}
                disabled={saving || uploadingImage}
                className="category-master-save inline-flex items-center justify-center gap-2 px-6 text-sm font-semibold"
              >
                {saving && (
                  <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                )}
                {saving ? "Saving..." : id ? "Update category" : "Save category"}
              </button>
            </div>
          </div>

          {/* List */}
          <div className="bg-white rounded-2xl shadow-sm border overflow-hidden category-master-list">
            <table className="w-full text-sm">
              <thead className="bg-gray-50">
                <tr className="text-left text-gray-500">
                <th className="p-4">Image</th>

                  <th className="p-4">Category</th>
                  <th className="p-4">Status</th>
                  <th className="p-4 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {list.map((item) => (
                  <tr
                    key={item.cat_id}
                    className="border-t hover:bg-gray-50 transition"
                  >
                     <td className="p-4 font-medium text-gray-900">
                     {item.cat_img && (
                  <div className="flex items-center gap-3">
                    <img
                      src={item.cat_img}
                      alt={item.cat_name}
                      className="w-14 h-14 rounded-lg object-cover border"
                    />

                
                  </div>
                )}
                    </td>
                    <td className="p-4 font-medium text-gray-900">
                      {item.cat_name}
                    </td>

                    <td className="p-4">
                      <span className={`category-status-badge ${getCategoryStatus(item.cat_state) === "Active" ? "is-active" : "is-inactive"}`}>
                        {getCategoryStatus(item.cat_state)}
                      </span>
                    </td>

                    <td className="p-4 text-right">
                      <div className="flex justify-end gap-2">
                      <button
                        type="button"
                        onClick={() => {
                          setCategory(item.cat_name);
                          setStatus(getCategoryStatus(item.cat_state) === "Active" ? "Yes" : "No");
                          setId(String(item.cat_id));

                          setCategoryImage(item.cat_img || null);
                          setCategoryImagePreview(item.cat_img || "");
                        }}
                        className="category-row-action inline-flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 py-2 text-gray-600 hover:bg-gray-50 hover:text-gray-900"
                        disabled={saving || deletingId === String(item.cat_id)}
                      >
                        <Pencil size={14} aria-hidden="true" />
                        Edit
                      </button>

                      <button
                        type="button"
                        onClick={() => {
                          setAlertDetails({
                            status: true,
                            title: "Warning!",
                            message: `You’re about to delete service of ${item.cat_name}`,
                            icon: IoTrashOutline,
                            button1Text: "Cancel",
                            button2Text: "Delete Service",
                            button1Style: "px-4 py-2 rounded-lg bg-gray-100 text-black hover:bg-gray-200 transition",
                            button2Style: "px-4 py-2 rounded-lg bg-red-600 text-white hover:bg-red-700 transition",
                            onButton1Click: () => console.log("Saved "),
                            onButton2Click: () => {
                              // setLoading(true)
                              CallAPIPostPromise(`/category/delete-category/`,{
                                catId:item.cat_id
                              }).then(()=>{
                               
                              setRefresh(r=>!r)
                                
                              }).catch((e)=>{
                                console.log(e)
                              }).finally(()=>{
                              })
                            },
                          })
                        }}
                        className="category-row-action category-row-action--delete inline-flex items-center gap-1.5 rounded-lg border border-red-200 bg-transparent px-3 py-2 text-red-600 hover:bg-red-50"
                        disabled={saving || deletingId === String(item.cat_id)}
                      >
                        <Trash2 size={14} aria-hidden="true" />
                        Delete
                      </button>
                      </div>
                    </td>
                  </tr>
                ))}

                {list.length === 0 && (
                  <tr>
                    <td colSpan={4} className="p-6 text-center text-gray-400">
                      No categories yet
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
      </div>
      <TwoButtonAlert setDetails={setAlertDetails}  details={alertDetails}/>

    </div>
  );
};

export default CategoryMasterModern;
